import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { ComponentType, GlobalPositionStrategy, Overlay } from '@angular/cdk/overlay';
import { Injectable, Signal, inject } from '@angular/core';
import { ConfirmModalComponent, confirmModalResponse } from './confirm-modal/confirm-modal.component';
import { ModalContent } from './confirm-modal/confirm-modal.type';
import { ErrorModalComponent } from './error-modal/error-modal.component';
import { LoaderModalComponent } from './loader-modal/loader-modal.component';
import { ProgressModalComponent } from './progress-modal/progress-modal.component';
import { StatusModalComponent } from './status-modal/status-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly dialog = inject(Dialog);
  private readonly overlay = inject(Overlay);

  private topPosition: number = 24; // Default top position in rem for the modal.

  constructor() {
    window.addEventListener('resize', this.updateAllPositions, { passive: true });
    window.addEventListener('scroll', this.updateAllPositions, { passive: true });
  }

  openError(error: string, ...messages: string[]): Promise<void> {
    return this.openErrorModal(error, messages);
  }

  openDetailedError(error: string, details?: Record<string, string[]>): Promise<void> {
    return this.openErrorModal(error, undefined, details);
  }

  openSuccess(...messages: string[]): Promise<void> {
    return this.openStatus('success', 'Wooho! Det där gick ju bra.', 'Stäng', ...messages);
  }

  openInfo(title: string, buttonLabel: string = 'Stäng', ...messages: string[]): Promise<void> {
    return this.openStatus('none', title, buttonLabel, ...messages);
  }

  openWarning(title: string, ...messages: string[]): Promise<void> {
    return this.openStatus('warning', title, 'Stäng', ...messages);
  }

  openStatus(
    style: 'warning' | 'error' | 'success' | 'none',
    title: string,
    buttonLabel: string,
    ...messages: string[]
  ): Promise<void> {
    this.dialog.closeAll();
    return new Promise<void>((resolve) => {
      const ref = this.dialog.open(StatusModalComponent, {
        ...this.modalBaseConfig(),
        disableClose: true,
        data: { style: style, title: title, buttonLabel: buttonLabel, message: messages },
        positionStrategy: this.positionStrategy(),
      });
      ref.closed.subscribe(() => resolve());
    });
  }

  openConfirm(content?: ModalContent): Promise<confirmModalResponse> {
    this.dialog.closeAll();
    return new Promise<confirmModalResponse>((resolve) => {
      const dialogRef = this.dialog.open(ConfirmModalComponent, {
        ...this.modalBaseConfig(),
        disableClose: true,
        data: content,
        positionStrategy: this.positionStrategy(),
      });

      dialogRef.componentInstance.output.subscribe((v: confirmModalResponse) => resolve(v));
    });
  }

  openLoader(message: string): DialogRef<unknown> {
    this.dialog.closeAll();
    return this.dialog.open(LoaderModalComponent, {
      ...this.modalBaseConfig(),
      disableClose: true,
      data: { message: message },
      positionStrategy: this.positionStrategy(),
    });
  }

  openProgress(message: string, progress: Signal<number>, progress_message: Signal<string>): DialogRef<unknown> {
    this.dialog.closeAll();
    return this.dialog.open(ProgressModalComponent, {
      ...this.modalBaseConfig(),
      disableClose: true,
      data: { message: message, progress: progress, progress_message: progress_message, autoClose: 10 },
      positionStrategy: this.positionStrategy(),
    });
  }

  openCustom<C>(component: ComponentType<C>, data: unknown): DialogRef<unknown, C> {
    this.dialog.closeAll();
    return this.dialog.open(component, {
      ...this.modalBaseConfig(),
      disableClose: true,
      data,
      positionStrategy: this.positionStrategy(),
    }) as DialogRef<unknown, C>;
  }

  private openErrorModal(
    error: string,
    messages?: string[] | Record<string, string[]>,
    details?: Record<string, string[]>,
  ): Promise<void> {
    this.dialog.closeAll();
    return new Promise<void>((resolve) => {
      const ref = this.dialog.open(ErrorModalComponent, {
        ...this.modalBaseConfig(),
        disableClose: true,
        data: { error: error, message: messages, details: details },
        positionStrategy: this.positionStrategy(),
      });
      ref.closed.subscribe(() => resolve());
    });
  }

  private modalBaseConfig(): DialogConfig {
    return {
      disableClose: true,
      positionStrategy: this.positionStrategy(),
    };
  }

  private updateAllPositions = () => {
    const dialogs = this.dialog.openDialogs;
    if (!dialogs.length) return;
    dialogs.forEach((d) => {
      d.updatePosition();
      d.overlayRef.updatePositionStrategy(this.positionStrategy());
    });
  };

  private positionStrategy(): GlobalPositionStrategy {
    const strategy = this.overlay.position().global().centerHorizontally();
    const bigViewPort = window.innerHeight > 1200;
    if (bigViewPort) {
      return strategy.top(`${this.topPosition}rem`);
    } else {
      return strategy.centerVertically();
    }
  }

  closeAll(): void {
    this.dialog.closeAll();
  }
}
